/**
 * Generador del sello "Esta moto tiene mi huella": una huella dactilar cuyas
 * crestas están escritas con la firma de la persona, repetida y doblada
 * siguiendo cada curva, con degradado azul → morado, dentro de la plantilla
 * del sello (aro, texto central, cinta "MOTO PERFECTA" y marca).
 *
 * Todo se dibuja en un canvas cuadrado de `lado` px. Las medidas de la
 * plantilla son fracciones de ese lado, así la vista previa y la descarga
 * salen iguales a distinta resolución.
 */

import { CRESTAS_HUELLA, SEPARACION_CRESTAS, SILUETA_HUELLA } from './huellaCrestas'

export const LADO_DESCARGA = 2000

export const COLOR_AZUL = '#2456d6'
export const COLOR_MORADO = '#6a22b4'
const COLOR_TEXTO = '#23287a'
const COLOR_HUELLA_TEXTO = '#4a1b98'

export const FUENTE_TITULO = '"Montserrat", "Helvetica Neue", Arial, sans-serif'
export const FUENTE_SCRIPT = '"Kaushan Script", "Brush Script MT", cursive'

// Centro de las crestas y silueta de la huella (fracciones del lado). La
// silueta es un "huevo" cuadradito: domo arriba y costados que bajan rectos
// hasta perderse detrás de la cinta.
// La huella termina detrás de la cinta y no pasa del borde interior del aro.
const LIMITE_INFERIOR = 0.77
const RADIO_INTERIOR = 0.432

// Ajustes por defecto del generador.
// El alto de cada firma no se elige: sale de la separación entre crestas.
export const OPCIONES_DEFECTO = {
  // Grosor del trazo de la firma relativo a su alto: fijo al 90% del rango
  // que tenía el control (0.04 a 0.14), que es como mejor se lee la huella.
  grosorTrazo: 0.13,
}

// ==================== Utilidades ====================

/** PRNG determinista: misma firma, misma huella. */
const mulberry32 = (semilla) => {
  let a = semilla >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const semillaDeTrazos = (trazos) =>
  trazos.reduce(
    (h, trazo) => trazo.reduce((acc, p) => (Math.imul(acc ^ Math.round(p.x * 7 + p.y * 13), 2654435761) >>> 0), h),
    2166136261,
  )

// Proporción (ancho / alto) permitida para cada copia de la firma. Una firma
// casi plana (una raya) o casi vertical se lleva a estos límites para que
// cada copia tenga un tamaño razonable sobre la cresta.
const ASPECTO_MAX = 6
const ASPECTO_MIN = 0.5
// Distancia máxima entre puntos de un trazo (en altos de firma): así hasta
// una línea recta de dos puntos se dobla siguiendo la curva de la cresta.
const PASO_REMUESTREO = 0.06

const remuestrear = (trazo) => {
  const salida = [trazo[0]]
  for (let i = 1; i < trazo.length; i += 1) {
    const a = trazo[i - 1]
    const b = trazo[i]
    const n = Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / PASO_REMUESTREO)
    for (let k = 1; k <= n; k += 1) salida.push({ x: a.x + ((b.x - a.x) * k) / n, y: a.y + ((b.y - a.y) * k) / n })
  }
  return salida
}

/**
 * Pasa la firma (trazos en px del recuadro donde se firmó) a unidades: alto 1,
 * y de 0 (arriba) a 1 (abajo), x desde 0. La proporción se acota a
 * [ASPECTO_MIN, ASPECTO_MAX] centrando la firma, y los trazos se remuestrean.
 * Devuelve null si no hay firma.
 */
export const normalizarFirma = (trazos) => {
  const puntos = trazos.flat()
  if (puntos.length < 2) return null
  const xs = puntos.map((p) => p.x)
  const ys = puntos.map((p) => p.y)
  const minX = Math.min(...xs)
  const minY = Math.min(...ys)
  const rangoX = Math.max(Math.max(...xs) - minX, 1)
  const rangoY = Math.max(Math.max(...ys) - minY, 1)
  const alto = Math.max(rangoY, rangoX / ASPECTO_MAX)
  const ancho = Math.max(rangoX, alto * ASPECTO_MIN)
  const offX = (ancho - rangoX) / 2
  const offY = (alto - rangoY) / 2
  return {
    aspecto: ancho / alto,
    trazos: trazos
      .filter((t) => t.length)
      .map((t) => remuestrear(t.map((p) => ({ x: (p.x - minX + offX) / alto, y: (p.y - minY + offY) / alto })))),
    semilla: semillaDeTrazos(trazos),
  }
}

const degradado = (ctx, x0, y0, x1, y1, colores = [COLOR_AZUL, COLOR_MORADO]) => {
  const g = ctx.createLinearGradient(x0, y0, x1, y1)
  colores.forEach((c, i) => g.addColorStop(i / (colores.length - 1), c))
  return g
}

// ==================== Texto central ====================

/**
 * Posición del texto central y la caja que ocupa (para que las crestas lo
 * rodeen sin pasarle por encima). Se mide con el contexto, así la caja se
 * ajusta a las fuentes que realmente cargaron.
 */
const maquetarTextoCentral = (ctx, L) => {
  const tamTitulo = L * 0.052
  const tamScript = L * 0.112
  const lineas = [
    { texto: 'Esta moto', y: L * 0.345 },
    { texto: 'tiene mi', y: L * 0.394 },
  ]
  ctx.font = `800 ${tamTitulo}px ${FUENTE_TITULO}`
  const anchoTitulo = Math.max(...lineas.map((l) => ctx.measureText(l.texto).width))
  ctx.font = `${tamScript}px ${FUENTE_SCRIPT}`
  const anchoScript = ctx.measureText('huella').width
  const huella = { y: L * 0.49, tam: tamScript }
  const subrayado = { y: L * 0.518 }
  const ancho = Math.max(anchoTitulo, anchoScript * 1.08)
  const pad = L * 0.008
  return {
    tamTitulo,
    lineas,
    huella,
    subrayado,
    caja: {
      x0: L / 2 - ancho / 2 - pad,
      x1: L / 2 + ancho / 2 + pad,
      y0: lineas[0].y - tamTitulo * 0.8 - pad,
      y1: subrayado.y + L * 0.02 + pad,
      radio: L * 0.035,
    },
  }
}

const dentroCajaRedondeada = (x, y, c) => {
  if (x < c.x0 || x > c.x1 || y < c.y0 || y > c.y1) return false
  const dx = Math.max(c.x0 + c.radio - x, 0, x - (c.x1 - c.radio))
  const dy = Math.max(c.y0 + c.radio - y, 0, y - (c.y1 - c.radio))
  return dx * dx + dy * dy <= c.radio * c.radio
}

const dibujarTextoCentral = (ctx, L, m) => {
  ctx.save()
  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = COLOR_TEXTO
  ctx.font = `800 ${m.tamTitulo}px ${FUENTE_TITULO}`
  m.lineas.forEach((l) => ctx.fillText(l.texto, L / 2, l.y))

  // "huella" en script; un trazo del mismo color la engruesa como pincel.
  ctx.font = `${m.huella.tam}px ${FUENTE_SCRIPT}`
  ctx.fillStyle = degradado(ctx, L * 0.36, 0, L * 0.64, 0, ['#3b2aa0', COLOR_HUELLA_TEXTO])
  ctx.strokeStyle = ctx.fillStyle
  ctx.lineWidth = L * 0.0035
  ctx.lineJoin = 'round'
  ctx.strokeText('huella', L / 2, m.huella.y)
  ctx.fillText('huella', L / 2, m.huella.y)

  // Subrayado de pincel: media luna que se afina en las puntas.
  const y = m.subrayado.y
  ctx.fillStyle = COLOR_HUELLA_TEXTO
  ctx.beginPath()
  ctx.moveTo(L * 0.365, y + L * 0.015)
  ctx.quadraticCurveTo(L * 0.5, y - L * 0.022, L * 0.652, y - L * 0.022)
  ctx.quadraticCurveTo(L * 0.5, y + L * 0.004, L * 0.365, y + L * 0.015)
  ctx.closePath()
  ctx.fill()
  ctx.restore()
}

// ==================== Crestas de la huella ====================

const dentroAro = (x, y, L) => y <= LIMITE_INFERIOR * L && Math.hypot(x - L / 2, y - L / 2) <= RADIO_INTERIOR * L

/**
 * Crestas del diseño original (huellaCrestas.js) llevadas a px: se remuestrean
 * a pasos cortos y se cortan donde chocan con la caja del texto central o
 * salen del aro. Quedan como tramos con su largo acumulado.
 */
const generarCrestas = (L, paso, largoMinimo, cajaTexto) => {
  const tramos = []
  CRESTAS_HUELLA.forEach((cresta) => {
    let tramo = []
    const cerrar = () => {
      if (tramo.length > 1) tramos.push(tramo)
      tramo = []
    }
    for (let i = 0; i < cresta.length; i += 1) {
      const a = { x: cresta[i].x * L, y: cresta[i].y * L }
      const b = cresta[i + 1] ? { x: cresta[i + 1].x * L, y: cresta[i + 1].y * L } : null
      const n = b ? Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / paso)) : 1
      for (let k = 0; k < n; k += 1) {
        const x = b ? a.x + ((b.x - a.x) * k) / n : a.x
        const y = b ? a.y + ((b.y - a.y) * k) / n : a.y
        if (dentroAro(x, y, L) && !dentroCajaRedondeada(x, y, cajaTexto)) tramo.push({ x, y })
        else cerrar()
      }
    }
    cerrar()
  })
  return tramos
    .map((puntos) => {
      const acumulado = [0]
      for (let k = 1; k < puntos.length; k += 1) {
        acumulado.push(acumulado[k - 1] + Math.hypot(puntos[k].x - puntos[k - 1].x, puntos[k].y - puntos[k - 1].y))
      }
      return { puntos, acumulado, largo: acumulado[acumulado.length - 1] }
    })
    .filter((c) => c.largo >= largoMinimo)
}

/** Punto y tangente a una distancia `s` del inicio de la cresta. */
const puntoEn = (cresta, s) => {
  const { puntos, acumulado } = cresta
  let lo = 0
  let hi = acumulado.length - 1
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (acumulado[mid] <= s) lo = mid
    else hi = mid
  }
  const a = puntos[lo]
  const b = puntos[hi]
  const tramo = acumulado[hi] - acumulado[lo] || 1
  const f = Math.min(Math.max((s - acumulado[lo]) / tramo, 0), 1)
  const tx = (b.x - a.x) / tramo
  const ty = (b.y - a.y) / tramo
  const n = Math.hypot(tx, ty) || 1
  return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, tx: tx / n, ty: ty / n }
}

/**
 * Dibuja la firma repetida a lo largo de cada cresta. Cada punto de la firma
 * se dobla con la curva: x avanza por la cresta, y se desplaza por la normal.
 * Arriba y en los costados la parte de arriba de la firma mira hacia afuera;
 * donde la cresta va hacia la izquierda (la parte de abajo) la copia se gira
 * para que se lea derecha, de izquierda a derecha.
 * Con varias firmas se reparten como una baraja: se usan todas en orden
 * aleatorio antes de repetir, así no se juntan copias de la misma.
 */
const dibujarFirmasEnCrestas = (ctx, firmas, L, opciones, cajaTexto, aleatorio) => {
  let baraja = []
  const siguiente = () => {
    if (!baraja.length) {
      baraja = firmas.map((_, i) => i)
      for (let i = baraja.length - 1; i > 0; i -= 1) {
        const j = Math.floor(aleatorio() * (i + 1))
        ;[baraja[i], baraja[j]] = [baraja[j], baraja[i]]
      }
    }
    return firmas[baraja.pop()]
  }
  const aspectoMedio = firmas.reduce((acc, f) => acc + f.aspecto, 0) / firmas.length
  // La firma ocupa poco más de la mitad del espacio entre crestas: el canal
  // blanco que queda entre ellas es lo que hace que se lea la forma de la huella
  // (como en el diseño original), aunque la firma sea enredada.
  const alto = SEPARACION_CRESTAS * L * 0.62
  const hueco = alto * 0.15
  const crestas = generarCrestas(L, alto * 0.2, Math.min(aspectoMedio, 2) * alto * 0.5, cajaTexto)

  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = '#000'
  crestas.forEach((cresta) => {
    // La primera copia arranca antes del inicio de la cresta y se recorta: así
    // la cresta queda llena desde el primer píxel aunque la firma sea larga.
    let s0 = -aleatorio() * (aspectoMedio * alto + hueco)
    while (s0 < cresta.largo) {
      const firma = siguiente()
      // Pequeña variación de tamaño para que no parezca copiado y pegado.
      const escala = alto * (0.92 + aleatorio() * 0.14)
      const ancho = firma.aspecto * escala
      const medio = puntoEn(cresta, Math.min(s0 + ancho / 2, cresta.largo))
      const girada = medio.tx < -0.3
      ctx.lineWidth = Math.max(escala * opciones.grosorTrazo, 0.6)
      firma.trazos.forEach((trazo) => {
        ctx.beginPath()
        let empezado = false
        trazo.forEach((p) => {
          const s = girada ? s0 + ancho - p.x * escala : s0 + p.x * escala
          if (s > cresta.largo || s < 0) return
          const q = puntoEn(cresta, s)
          // (-ty, tx) apunta hacia adentro de la huella. Normal: la parte de
          // arriba de la firma (y = 0) va hacia afuera; girada, hacia adentro.
          const d = (p.y - 0.5) * escala * (girada ? -1 : 1)
          const x = q.x - q.ty * d
          const y = q.y + q.tx * d
          if (empezado) ctx.lineTo(x, y)
          else {
            ctx.moveTo(x, y)
            empezado = true
          }
        })
        ctx.stroke()
      })
      s0 += ancho + hueco
    }
  })
}

// ==================== Plantilla ====================

const dibujarBase = (ctx, L) => {
  // Disco blanco con sombra suave y aro con degradado.
  ctx.save()
  ctx.shadowColor = 'rgba(15, 23, 42, 0.28)'
  ctx.shadowBlur = L * 0.025
  ctx.shadowOffsetY = L * 0.008
  ctx.fillStyle = '#ffffff'
  ctx.beginPath()
  ctx.arc(L / 2, L / 2, L * 0.485, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  ctx.save()
  ctx.strokeStyle = degradado(ctx, L * 0.05, L * 0.4, L * 0.95, L * 0.6, ['#2a4fd0', '#4a3cc8', '#6a22b4'])
  ctx.lineWidth = L * 0.019
  ctx.beginPath()
  ctx.arc(L / 2, L / 2, L * 0.452, 0, Math.PI * 2)
  ctx.stroke()
  ctx.restore()
}

/** Punto, tangente y largo acumulado de una curva cuadrática muestreada. */
const muestrearCuadratica = (p0, c, p2, n = 120) => {
  const puntos = []
  let largo = 0
  for (let i = 0; i <= n; i += 1) {
    const t = i / n
    const x = (1 - t) ** 2 * p0.x + 2 * (1 - t) * t * c.x + t ** 2 * p2.x
    const y = (1 - t) ** 2 * p0.y + 2 * (1 - t) * t * c.y + t ** 2 * p2.y
    if (i) largo += Math.hypot(x - puntos[i - 1].x, y - puntos[i - 1].y)
    puntos.push({ x, y, s: largo })
  }
  return { puntos, largo }
}

/** Escribe un texto centrado siguiendo una curva, letra por letra. */
const textoEnCurva = (ctx, texto, curva, espaciado) => {
  const anchos = [...texto].map((ch) => ctx.measureText(ch).width + espaciado)
  const total = anchos.reduce((a, b) => a + b, 0) - espaciado
  let s = (curva.largo - total) / 2
  ;[...texto].forEach((ch, i) => {
    const sm = s + (anchos[i] - espaciado) / 2
    const k = curva.puntos.findIndex((p) => p.s >= sm)
    const a = curva.puntos[Math.max(k - 1, 0)]
    const b = curva.puntos[Math.max(k, 1)]
    ctx.save()
    ctx.translate(a.x, a.y)
    ctx.rotate(Math.atan2(b.y - a.y, b.x - a.x))
    ctx.fillText(ch, 0, 0)
    ctx.restore()
    s += anchos[i]
  })
}

/** Cinta curva (sonrisa) con colas plegadas y el texto siguiendo el arco. */
const dibujarCinta = (ctx, L) => {
  // Misma posición y curva que la cinta del diseño original: puntas en 0.70
  // y centro del borde superior en 0.745 (la cuadrática baja curvatura / 2).
  const h = L * 0.076
  const x0 = L * 0.215
  const x1 = L * 0.785
  const yExt = L * 0.7
  const curvatura = L * 0.09
  const cola = L * 0.09
  ctx.save()

  // Colas detrás de la cinta: suben hacia afuera siguiendo la curva, con
  // corte en V, y un pliegue oscuro que las une a la cara principal.
  ;[
    { borde: x0, lado: -1, color: '#22309a', pliegue: '#131d66' },
    { borde: x1, lado: 1, color: '#4f22a2', pliegue: '#2c1066' },
  ].forEach(({ borde, lado, color, pliegue }) => {
    const interior = borde - lado * cola * 0.12
    const exterior = borde + lado * cola
    const topInt = yExt + h * 0.32
    const topExt = yExt - h * 0.3
    ctx.fillStyle = color
    ctx.beginPath()
    ctx.moveTo(interior, topInt)
    ctx.lineTo(exterior, topExt)
    ctx.lineTo(exterior - lado * cola * 0.3, topExt + h * 0.5)
    ctx.lineTo(exterior, topExt + h)
    ctx.lineTo(interior, topInt + h)
    ctx.closePath()
    ctx.fill()
    ctx.fillStyle = pliegue
    ctx.beginPath()
    ctx.moveTo(borde, yExt + h * 0.55)
    ctx.lineTo(borde, yExt + h)
    ctx.lineTo(interior, topInt + h)
    ctx.closePath()
    ctx.fill()
  })

  // Cara principal: dos cuadráticas paralelas.
  const control = { x: L / 2, y: yExt + curvatura }
  ctx.fillStyle = degradado(ctx, x0, 0, x1, 0, ['#2a46c9', '#3b33b6', '#5a21a6'])
  ctx.beginPath()
  ctx.moveTo(x0, yExt)
  ctx.quadraticCurveTo(control.x, control.y, x1, yExt)
  ctx.lineTo(x1, yExt + h)
  ctx.quadraticCurveTo(control.x, control.y + h, x0, yExt + h)
  ctx.closePath()
  ctx.fill()
  // Brillo sutil en el borde superior.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)'
  ctx.lineWidth = L * 0.003
  ctx.beginPath()
  ctx.moveTo(x0, yExt + L * 0.004)
  ctx.quadraticCurveTo(control.x, control.y + L * 0.004, x1, yExt + L * 0.004)
  ctx.stroke()

  ctx.fillStyle = '#ffffff'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `800 ${L * 0.054}px ${FUENTE_TITULO}`
  const base = h * 0.54
  const curva = muestrearCuadratica(
    { x: x0, y: yExt + base },
    { x: control.x, y: control.y + base },
    { x: x1, y: yExt + base },
  )
  textoEnCurva(ctx, 'MOTO PERFECTA', curva, L * 0.002)
  ctx.restore()
}

// Logo oficial bajo la cinta: ancho como fracción del lado y borde superior.
const LOGO_ANCHO = 0.165
const LOGO_ARRIBA = 0.828

/**
 * Logo de Auteco bajo la cinta. Con `logo` (imagen oficial) se dibuja tal
 * cual; si no cargó, se usa una versión dibujada de respaldo (techo azul con
 * punto rojo y "auteco").
 */
const dibujarMarca = (ctx, L, logo) => {
  if (logo?.naturalWidth) {
    const w = L * LOGO_ANCHO
    const h = (w * logo.naturalHeight) / logo.naturalWidth
    // El ® queda a la derecha de la palabra: se corre un poco para que
    // "auteco" quede centrada.
    ctx.drawImage(logo, L / 2 - w / 2 + w * 0.03, L * LOGO_ARRIBA, w, h)
    return
  }
  ctx.save()
  const cx = L * 0.5
  ctx.fillStyle = '#1f4fa8'
  ctx.beginPath()
  ctx.moveTo(cx - L * 0.05, L * 0.872)
  ctx.lineTo(cx, L * 0.838)
  ctx.lineTo(cx + L * 0.05, L * 0.872)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = '#e1251b'
  ctx.beginPath()
  ctx.ellipse(cx, L * 0.86, L * 0.012, L * 0.009, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#1f4fa8'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'
  ctx.font = `800 ${L * 0.05}px ${FUENTE_TITULO}`
  ctx.fillText('auteco', cx, L * 0.914)
  ctx.restore()
}

/** Espera a que carguen las fuentes del sello (si no, el canvas usa las de respaldo). */
export const cargarFuentesHuella = async () => {
  if (!document.fonts?.load) return
  try {
    await Promise.all([
      document.fonts.load(`800 40px ${FUENTE_TITULO}`),
      document.fonts.load(`40px ${FUENTE_SCRIPT}`),
    ])
  } catch {
    // Sin las fuentes se dibuja igual con las de respaldo.
  }
}

/**
 * Dibuja el sello completo en `canvas` (cuadrado de `lado` px).
 * `plantilla`: imagen opcional de diseño (PNG con la zona de la huella
 * transparente); si viene, reemplaza aro, textos, cinta y marca dibujados.
 * `logo`: imagen ya cargada del logo de Auteco (si no, se usa el de respaldo).
 */
export const dibujarSelloHuella = (
  canvas,
  { trazos, firmas = null, lado, opciones = OPCIONES_DEFECTO, plantilla = null, logo = null },
) => {
  canvas.width = lado
  canvas.height = lado
  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, lado, lado)
  if (!plantilla) dibujarBase(ctx, lado)
  const texto = maquetarTextoCentral(ctx, lado)

  // `firmas` (lista de firmas, cada una con sus trazos) tiene prioridad sobre
  // `trazos` (una sola firma): con varias, la huella se arma con todas.
  const normalizadas = (firmas?.length ? firmas : [trazos])
    .filter((t) => t?.length)
    .map(normalizarFirma)
    .filter(Boolean)
  if (normalizadas.length) {
    const semilla = normalizadas.reduce((h, f) => Math.imul(h ^ f.semilla, 2654435761) >>> 0, 2166136261)
    // La huella se dibuja en negro en otra capa y luego se le aplica el
    // degradado con 'source-in' (solo pinta donde ya hay trazo).
    const capa = document.createElement('canvas')
    capa.width = lado
    capa.height = lado
    const c = capa.getContext('2d')
    // Recorte con la silueta exacta del diseño original.
    c.beginPath()
    SILUETA_HUELLA.forEach((p, i) => (i ? c.lineTo(p.x * lado, p.y * lado) : c.moveTo(p.x * lado, p.y * lado)))
    c.closePath()
    c.clip()
    dibujarFirmasEnCrestas(c, normalizadas, lado, { ...OPCIONES_DEFECTO, ...opciones }, texto.caja, mulberry32(semilla))
    c.globalCompositeOperation = 'source-in'
    // Degradado horizontal: azul rey a la izquierda, violeta a la derecha.
    c.fillStyle = degradado(c, lado * 0.18, lado * 0.4, lado * 0.82, lado * 0.5, ['#1c5ae6', '#3f52e0', '#6a3bd6', '#7d2ccc'])
    c.fillRect(0, 0, lado, lado)
    ctx.drawImage(capa, 0, 0)
  }

  if (plantilla) {
    ctx.drawImage(plantilla, 0, 0, lado, lado)
  } else {
    dibujarTextoCentral(ctx, lado, texto)
    dibujarCinta(ctx, lado)
    dibujarMarca(ctx, lado, logo)
  }
}
